import {useState} from "react";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {Table} from "@/components/table/Table.tsx";
import {TextAlignment} from "@/components/table/Table.types.ts";
import {shortDateTimeUser} from "@/lib/format.tsx";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {LocationStatus, StatusPerLocationResponse} from "@/lib/queries/checks.ts";
import {ALERT_STATE_TO_PILL_STATUS} from "@/lib/types/alerting.ts";
import {Service} from "@/lib/types/checks.ts";
import {ServiceDiagnostics, ServiceDiagnosticStatus} from "@/lib/types/monitoring.ts";
import {CheckOutput} from "@/pages/checks/rta/components/CheckOutput.tsx";
import {StatusPerLocationRealtime} from "@/pages/checks/rta/components/StatusPerLocationRealtime.tsx";

import "./StatusPerLocation.css";

type StatusPerLocationProps = {
  service: Service;
  statusPerLocation: StatusPerLocationResponse;
};

export const StatusPerLocation = ({service, statusPerLocation}: StatusPerLocationProps) => {
  const {user: currentUser} = useCurrentUserContext();
  const [realtimeData, setRealtimeData] = useState<ServiceDiagnostics | null>(null);
  const [locationIdMap, setLocationIdMap] = useState<{[k: string]: number} | null>(null);

  const hasRealTimeStatus = statusPerLocation.items.some((l) => l.is_nagios);

  const loadRealTimeCheckStatus = async (firstRun = true) => {
    const response = await fetch(
      `${URL_ORIGINAL_WEBSITE}/devices/services/${service.id}/analysis/ajax/load-nagios-status`,
      {
        method: firstRun ? "POST" : "GET",
        credentials: "include",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const diagnostics = (await response.json()) as ServiceDiagnostics;

    setRealtimeData(diagnostics);

    if (diagnostics?.status === ServiceDiagnosticStatus.RUNNING) {
      setTimeout(() => loadRealTimeCheckStatus(false), 500);
    } else if (diagnostics?.status === ServiceDiagnosticStatus.COMPLETED) {
      setLocationIdMap(
        Object.fromEntries(
          statusPerLocation.items.map((monitoringGroup, index) => [monitoringGroup.monitoring_server.id, index])
        )
      );
    }
  };

  if (statusPerLocation.items.length === 0) return null;

  return (
    <div className="status-per-location mb-4">
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="heading fw-bold my-4">Location Status</h5>
        <div>
          {hasRealTimeStatus && realtimeData?.status !== ServiceDiagnosticStatus.COMPLETED && (
            <Button
              disabled={realtimeData?.status === ServiceDiagnosticStatus.RUNNING}
              onClick={() => loadRealTimeCheckStatus()}
            >
              {realtimeData?.status === ServiceDiagnosticStatus.RUNNING
                ? "Loading Real-Time Check Status..."
                : "Load Real-Time Check Status"}
            </Button>
          )}
        </div>
      </div>
      <Table
        subrowRenderer={(status: LocationStatus) =>
          locationIdMap ? (
            <StatusPerLocationRealtime
              diagnosticData={realtimeData?.data[locationIdMap![status.monitoring_server.id]]}
            />
          ) : null
        }
        rows={statusPerLocation.items}
        specs={[
          {
            fieldName: "state",
            fieldTitle: "State",
            meta: {columnClassName: "text-center", headerTextAlignment: TextAlignment.Center},
            accessor: ({latest_alert}: LocationStatus) => (
              <StatusPill
                type="incident"
                variant="textAndIcon"
                status={
                  !latest_alert || latest_alert.is_up ? PillStatus.UP : ALERT_STATE_TO_PILL_STATUS[latest_alert.state]
                }
              />
            ),
          },
          {
            fieldName: "location",
            fieldTitle: "Location",
            accessor: (status: LocationStatus) => status.monitoring_group.location,
          },
          {
            fieldName: "probe_server",
            fieldTitle: "Assigned Probe Server",
            accessor: (status: LocationStatus) => {
              let specialStateText = null;
              if (service.is_paused) {
                specialStateText = "Check paused";
              } else if (!status.monitoring_server) {
                specialStateText = status.is_nagios ? "Pending reconfiguration..." : "All location probes";
              }

              if (specialStateText) return <em className="text-muted">{specialStateText}</em>;

              return (
                <>
                  {status.monitoring_server.name}
                  <br />
                  <small className="text-nowrap text-muted font-monospace font-12">
                    {status.monitoring_server.address_ipv4}
                  </small>
                  <br />
                  <small className="text-nowrap text-muted font-monospace font-12">
                    {status.monitoring_server.address_ipv6}
                  </small>
                </>
              );
            },
          },
          {
            fieldName: "last_alert_date",
            fieldTitle: "Last Alert Date",
            accessor: (status: LocationStatus) =>
              status.latest_alert ? shortDateTimeUser(status.latest_alert?.created_at, currentUser!) : null,
          },
          {
            fieldName: "last_alert_details",
            fieldTitle: "Last Alert Details",
            accessor: (status: LocationStatus) =>
              status.latest_alert ? <CheckOutput output={status.latest_alert.output} /> : null,
          },
        ]}
      />
    </div>
  );
};
